import {
  DefaultFetchError,
  FetchCreate, FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate, useFetchDelete,
  useFetchGet,
  useFetchGetId, useFetchUpdate,
} from '../fetch';

export interface IService extends ServicesCreateParam {
  id: string;
  createdAt: string;
  updatedAt: string;
  usedAt: string | null;
}

export interface ServicesCreateParam {
  name: string;
  value: number;
  valueCHF: number;
  units: ServiceUnitEnum;
}

export enum ServiceUnitEnum {
  pieces = 'Pcs (pieces)',
  kilograms = 'Kg (kilograms)',
  hours = 'H (hours)'
}

export enum ServiceUnitShortEnum {
  pieces = 'Pcs',
  kilograms = 'Kg',
  hours = 'H'
}

export interface DefaultServices {
  id?: string;
  deliveryMultiplayer: number;
  platformUsageFee: number;
  insuranceFee: number;
}

interface ServicesGetParams extends PagingParams {
  orderByColumn?: 'name';
}

export const useServicesGet = <DD = ServicesTableData>(
  decorateData?: (data: PagingDataResponse<IService>) => DD,
): FetchGet<
    PagingDataResponse<IService>,
    ServicesGetParams,
    DefaultFetchError,
    DD
  > => useFetchGet(
    'admin/services',
    { autoStart: false, startStateLoading: false, decorateData },
  );

export interface IServiceTable extends IService {
    key: string;
  }

export interface ServicesTableData {
    data: IServiceTable[];
    total: number;
  }

export const useServicesTableGet = () => useServicesGet<ServicesTableData>(
  ({ data, meta }: PagingDataResponse<IService>): ServicesTableData => ({
    data: data.map((item: IService): IServiceTable => ({
      key: item.id,
      ...item,
    })),
    total: meta.itemCount,
  }),
);

export const useServicesGetById = <DD = IService>(
  id?: string,
  decorateData?: (data: IService) => DD,
): FetchGetId<
    IService,
    DefaultFetchError,
    unknown,
    DD
  > => useFetchGetId(
    'admin/services',
    id,
    {
      autoStart: !!id,
      decorateData,
    },
  );

export const useServicesCreate = (): FetchCreate<FetchSuccess, DefaultFetchError, ServicesCreateParam> => (
  useFetchCreate('admin/services')
);

export type ServicesUpdateParams = IService;

export const useServicesUpdate = (id?: string): FetchUpdate<
    FetchSuccess, DefaultFetchError, ServicesUpdateParams> => (
  useFetchUpdate('admin/services', id)
);

export const useServicesDelete = (id?: string): FetchDelete<
    FetchSuccess, DefaultFetchError
  > => (
  useFetchDelete('admin/services', id)
);

export const useDefaultServicesGet = (): FetchGetId<
    DefaultServices,
    DefaultFetchError,
    unknown,
    DefaultServices
  > => useFetchGet('admin/settings', { cacheLifetime: 3000, multiple: 'default-services-get' });

export const useDefaultServicesUpdate = (): FetchUpdate<
    FetchSuccess, DefaultFetchError, DefaultServices> => (
  useFetchUpdate('admin/settings', '')
);

export const useServiceUsedNow = (id?: string): FetchCreate<IService, DefaultFetchError> => (
  useFetchCreate(`admin/services${id ? `${id}/used-now` : ''}`) // ${id} used-now
);
